/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { isEmpty, isEqual } from 'lodash'
import React, { Component, Fragment } from 'react'
import { defineMessages, injectIntl } from 'react-intl'

import { EuiFilterButton, EuiIconTip, EuiSkeletonText, EuiSpacer } from '@elastic/eui'

import type {
  DeploymentsSearchResponse,
  DeploymentSearchResponse,
  SearchRequest,
} from '@modules/cloud-api/v1/types'
import type { AsyncRequestState, ProfileState, UserSubscription } from '@modules/ui-types'
import {
  getFilterQueryString,
  isEmptyQuery,
  setFilterQueryString,
} from '@modules/cui/FilterContext'
import { CuiAlert } from '@modules/cui/Alert'
import type { OnFilterChangeParams } from '@modules/cui/FilterContext'
import Header from '@modules/cui/Header'
import LocalStorageKey from '@modules/utils/localStorageKeys'
import { startPageActions } from '@modules/utils/apm'

import CreateStackDeploymentButton from '@/components/StackDeploymentSearch/CreateStackDeploymentButton'
import { deploymentsCrumbs } from '@/lib/crumbBuilder'
import { disableDeploymentButton, disableDeploymentCreation } from '@/lib/deployments/newDeployment'
import { isMarketPlaceUser, isUnSubscribedMarketPlaceUser } from '@/lib/marketPlace'
import { getConfigForKey } from '@/store'

import IlmTemplateMigrationCallout from '../IlmMigration/IlmTemplateMigrationCallout'
import UnSubscribedMarketPlaceUserCallout from '../MarketPlace/UnSubscribedMarketPlaceUserCallout'

import searchDeploymentsQuery from './searchDeploymentsQuery'
import TrialState from './TrialState'
import ExportDeploymentsModal from './ExportDeploymentsModal'
import DeploymentList from './DeploymentList'
import DeploymentFilterContext, { getEsQuery } from './DeploymentFilterContext'
import StackDeploymentEmptyState from './CreateStackDeploymentButton/StackDeploymentEmptyState'

import type { SortState, TableChangeState } from './types'
import type { WrappedComponentProps } from 'react-intl'

type StateProps = {
  deletedDeploymentIds: string[]
  searchResults: DeploymentsSearchResponse | null
  searchResultsRequest: AsyncRequestState
  profile: ProfileState
  subscription?: UserSubscription
  hideCreateClusterButton: boolean
  exportDeployments: boolean
  showIlmMigrationCallout: boolean
  showTitle?: boolean
  showBreadcrumbs?: boolean
}

type DispatchProps = {
  searchDeployments: (query: SearchRequest) => Promise<void>
}

type ConsumerProps = {
  storageKey?: string
}

export type Props = StateProps & DispatchProps & ConsumerProps & WrappedComponentProps

type State = {
  esQuery: any
  query: string | null
  queryUsedForSearch: string | null
  queryResults: DeploymentSearchResponse[]
  showExportModal: boolean
  displayMode: 'grid' | 'table'
  // Pagination state
  currentCursor?: string
  nextCursor?: string
  pageIndex: number
  hasNextPage: boolean
  cursorHistory: string[] // Stack of previous cursors for backward navigation
  tableSort?: SortState
  isSorting: boolean
}

const messages = defineMessages({
  displayGrid: {
    id: `displayMode-grid`,
    defaultMessage: `Switch to grid view`,
  },
  displayTable: {
    id: `displayMode-table`,
    defaultMessage: `Switch to table view`,
  },
  export: {
    id: `export-deployments`,
    defaultMessage: `Export deployments...`,
  },
  title: {
    id: `hosted-deployments.title`,
    defaultMessage: `Hosted deployments`,
  },
  eceTitle: {
    id: `deployments.title`,
    defaultMessage: `Deployments`,
  },
})

const defaultEsQuery = {
  bool: {
    must: [
      {
        match: {
          stopped: { query: `n`, operator: `and` },
        },
      },
    ],
  },
}

class StackDeploymentSearch extends Component<Props, State> {
  startedTransaction = false

  static defaultProps = {
    storageKey: `StackDeploymentSearch`,
  }

  state: State = this.getInitialState()

  getInitialState(): State {
    const { storageKey } = this.props
    const queryString = getFilterQueryString({ storageKey })
    const esQuery = getEsQuery(queryString)
    const displayMode = getStoredDisplayMode()

    return {
      esQuery,
      query: queryString ?? null,
      queryResults: [],
      queryUsedForSearch: null,
      showExportModal: false,
      displayMode,
      currentCursor: undefined,
      nextCursor: undefined,
      pageIndex: 0,
      hasNextPage: false,
      cursorHistory: [],
      tableSort: undefined,
      isSorting: false,
    }
  }

  componentDidMount() {
    this.search()
  }

  componentDidUpdate(_oldProps: Props, oldState: State) {
    const { searchResultsRequest, searchResults } = this.props
    const { esQuery } = this.state

    if (
      !this.startedTransaction &&
      (searchResultsRequest.error || (searchResults && searchResults.deployments))
    ) {
      this.startedTransaction = true

      startPageActions(`Deployment search`)
    }

    // Update pagination state when search results change
    if (searchResults && searchResults !== _oldProps.searchResults) {
      // Clear sorting loading flag when new data arrives
      if (this.state.isSorting) {
        this.setState({ isSorting: false })
      }

      // Only show next page if:
      // 1. There's a cursor (API says there might be more)
      // 2. AND current page has results
      // 3. AND current page is full (has pageSize results) - indicates more might exist
      const pageSize = 30 // Default page size
      const hasResults = searchResults.deployments.length > 0
      const isPageFull = searchResults.deployments.length >= pageSize
      const hasCursor = !!searchResults.cursor

      // If we got 0 results and we're not on page 0, we've gone too far
      // Navigate back to the previous page
      if (!hasResults && this.state.pageIndex > 0) {
        const { cursorHistory, pageIndex } = this.state
        const previousPageIndex = pageIndex - 1
        const previousCursor =
          previousPageIndex === 0 ? undefined : cursorHistory[previousPageIndex]

        this.setState({
          pageIndex: previousPageIndex,
          currentCursor: previousCursor,
          nextCursor: undefined,
          hasNextPage: false,
        })

        // Re-fetch the previous page
        this.search({ cursor: previousCursor })
        return
      }

      this.setState({
        nextCursor: searchResults.cursor,
        hasNextPage: hasCursor && hasResults && isPageFull,
      })
    }

    if (!isEqual(esQuery, oldState.esQuery)) {
      this.search({ resetPagination: true })
    }
  }

  render() {
    const {
      hideCreateClusterButton,
      searchResults,
      profile,
      subscription,
      showIlmMigrationCallout,
      showTitle = true,
      showBreadcrumbs = true,
      intl: { formatMessage },
    } = this.props

    const deployments = searchResults ? searchResults.deployments : null
    const hasDeployments = !isEmpty(deployments)
    const createDisabled = disableDeploymentCreation({ profile, hasDeployments })
    const trialUser = profile && profile.inTrial
    let restartTrial = false
    let disableCreateButton = false

    if (profile) {
      restartTrial = profile.canRestartTrial
      disableCreateButton = disableDeploymentButton({ profile })
    }

    const appFamily = getConfigForKey(`APP_FAMILY`)

    return (
      <div id='deployments'>
        {!subscription && trialUser && <TrialState deployments={deployments} profile={profile} />}

        {profile && isMarketPlaceUser(profile) && isUnSubscribedMarketPlaceUser(profile) && (
          <UnSubscribedMarketPlaceUserCallout profile={profile} />
        )}

        {showIlmMigrationCallout && <IlmTemplateMigrationCallout displayLink={true} />}

        {showTitle && (
          <Header
            name={
              appFamily === 'cloud-enterprise'
                ? formatMessage(messages.eceTitle)
                : formatMessage(messages.title)
            }
            breadcrumbs={showBreadcrumbs ? deploymentsCrumbs() : []}
            children={
              hasDeployments &&
              !hideCreateClusterButton && (
                <CreateStackDeploymentButton
                  createDisabled={createDisabled}
                  disabled={disableCreateButton}
                  restartTrial={restartTrial}
                />
              )
            }
          />
        )}

        {this.renderContent()}
      </div>
    )
  }

  renderContent() {
    const {
      intl: { formatMessage },
      hideCreateClusterButton,
      profile,
      searchResults,
      searchResultsRequest,
      exportDeployments,
    } = this.props

    const isLoading = searchResultsRequest.inProgress

    if (!searchResults && isLoading) {
      // show a loading spinner because of indeterminate state
      return <EuiSkeletonText />
    }

    const { query, queryUsedForSearch, displayMode } = this.state
    const deployments = searchResults ? searchResults.deployments : []

    const hasDeployments = !isEmpty(deployments)
    const createDisabled = disableDeploymentCreation({ profile, hasDeployments })
    let restartTrial = false
    let disableCreateButton = false

    if (profile) {
      restartTrial = profile.canRestartTrial
      disableCreateButton = disableDeploymentButton({ profile })
    }

    const showCreateFirstDeployment = isEmpty(deployments) && isDefaultEsQuery(queryUsedForSearch)

    if (!hideCreateClusterButton && showCreateFirstDeployment) {
      return (
        <StackDeploymentEmptyState
          createDisabled={createDisabled}
          disabled={disableCreateButton}
          restartTrial={restartTrial}
          hasExpiredTrial={profile?.hasExpiredTrial}
        />
      )
    }

    const tools = [
      displayMode === `table` ? (
        <EuiFilterButton
          key='display-mode-grid'
          css={{ width: `40px` }}
          onClick={this.toggleDisplayMode}
          aria-label={formatMessage(messages.displayGrid)}
        >
          <EuiIconTip content={formatMessage(messages.displayGrid)} type='grid' />
        </EuiFilterButton>
      ) : (
        <EuiFilterButton
          key='display-mode-table'
          css={{ width: `40px` }}
          onClick={this.toggleDisplayMode}
          aria-label={formatMessage(messages.displayTable)}
        >
          <EuiIconTip content={formatMessage(messages.displayTable)} type='visTable' />
        </EuiFilterButton>
      ),
    ]

    if (exportDeployments) {
      tools.unshift(
        <EuiFilterButton
          key='import-action'
          css={{ width: `40px` }}
          onClick={this.showExportModal}
          aria-label={formatMessage(messages.export)}
          data-test-id='deploymentExportButton'
        >
          <EuiIconTip content={formatMessage(messages.export)} type='importAction' />
        </EuiFilterButton>,
      )
    }

    const filterContext = (
      <DeploymentFilterContext
        query={query}
        onChange={this.onChange}
        deployments={deployments}
        isLoading={isLoading}
        tools={tools}
      />
    )

    return (
      <Fragment>
        {filterContext}

        {searchResultsRequest.error && (
          <Fragment>
            <EuiSpacer size='l' />
            <CuiAlert type='error'>{searchResultsRequest.error}</CuiAlert>
          </Fragment>
        )}

        {this.renderDeploymentList(isLoading)}
        {this.renderExportModal()}
      </Fragment>
    )
  }

  renderDeploymentList(isLoading: boolean) {
    const { queryResults, displayMode, queryUsedForSearch, pageIndex, hasNextPage, cursorHistory } =
      this.state

    if (isEmpty(queryResults)) {
      return null
    }

    const { searchResults, deletedDeploymentIds } = this.props
    const { match_count = null, return_count = null } = searchResults || {}
    const deployments = queryResults.filter(
      (deployment) => !deletedDeploymentIds.includes(deployment.id),
    )
    const maintainOrder = !isDefaultEsQuery(queryUsedForSearch)

    return (
      <Fragment>
        <EuiSpacer size='l' />

        <DeploymentList
          displayMode={displayMode}
          deployments={deployments}
          shownCount={return_count}
          matchCount={match_count}
          reQuery={this.search}
          maintainOrder={maintainOrder}
          pageIndex={pageIndex}
          hasNextPage={hasNextPage}
          onTableChange={this.onTableChange}
          tableSort={this.state.tableSort}
          isSorting={this.state.isSorting}
          cursorHistory={cursorHistory}
          isLoading={isLoading}
        />
      </Fragment>
    )
  }

  renderExportModal() {
    const { queryResults = [], showExportModal } = this.state

    if (!showExportModal) {
      return null
    }

    return <ExportDeploymentsModal deployments={queryResults} close={this.hideExportModal} />
  }

  onChange = ({
    query,
    queryText,
    queryResults,
  }: OnFilterChangeParams<DeploymentSearchResponse>) => {
    const { storageKey } = this.props

    this.setState({
      esQuery: getEsQuery(query),
      query: queryText,
      queryResults,
    })

    setFilterQueryString({ storageKey, queryText })
  }

  search = ({
    size,
    cursor,
    resetPagination = false,
  }: {
    size?: number
    cursor?: string
    resetPagination?: boolean
  } = {}) => {
    const { searchDeployments, deletedDeploymentIds } = this.props
    const { esQuery, tableSort } = this.state

    const query = searchDeploymentsQuery({
      esQuery,
      deletedDeploymentIds,
      size,
      cursor,
      sort: tableSort,
    })

    this.setState((prevState) => ({
      ...prevState,
      queryUsedForSearch: esQuery,
      ...(resetPagination && {
        pageIndex: 0,
        currentCursor: undefined,
        nextCursor: undefined,
        hasNextPage: false,
        cursorHistory: [],
      }),
    }))

    return searchDeployments(query)
  }

  onTableChange = (state: TableChangeState) => {
    const { page, sort } = state

    if (sort) {
      // Apply new sort, reset pagination to first page, and re-run search
      this.setState(
        {
          tableSort: sort,
          isSorting: true,
        },
        () => this.search({ resetPagination: true }),
      )
      return
    }

    const { pageIndex, cursorHistory } = this.state
    const newPageIndex = page.index

    if (newPageIndex === pageIndex) {
      // Same page, no action needed
      return
    }

    if (newPageIndex > pageIndex) {
      // Going forward (can only be next page with compressed pagination)
      this.goToNextPage()
    } else {
      // Going to previous page - use cursor history
      const targetHistoryIndex = newPageIndex

      if (targetHistoryIndex >= 0 && targetHistoryIndex < cursorHistory.length) {
        this.goToPreviousPage(targetHistoryIndex)
      } else {
        // If we don't have the cursor in history, reset to first page
        this.resetAndSearch()
      }
    }
  }

  goToNextPage = () => {
    const { nextCursor, pageIndex, currentCursor, cursorHistory } = this.state

    if (nextCursor) {
      // Store current cursor in history before moving to next page
      const newHistory = [...cursorHistory]
      // Ensure we have the right cursor for this page index
      newHistory[pageIndex] = currentCursor || '' // Store cursor that gets us to current page
      this.setState({
        pageIndex: pageIndex + 1,
        currentCursor: nextCursor,
        cursorHistory: newHistory,
      })
      this.search({ cursor: nextCursor })
    }
  }

  goToPreviousPage = (targetPageIndex: number) => {
    const { cursorHistory } = this.state

    // Get the cursor for the target page
    const targetCursor = targetPageIndex === 0 ? undefined : cursorHistory[targetPageIndex]
    this.setState({
      pageIndex: targetPageIndex,
      currentCursor: targetCursor,
    })
    this.search({ cursor: targetCursor })
  }

  resetAndSearch = () => {
    this.search({ resetPagination: true })
  }

  showExportModal = () => {
    this.search({ size: 2500 })
    this.setState({ showExportModal: true })
  }

  hideExportModal = () => {
    this.setState({ showExportModal: false })
  }

  toggleDisplayMode = () => {
    const { displayMode } = this.state
    const newDisplayMode = displayMode === `grid` ? `table` : `grid`

    this.setState({ displayMode: newDisplayMode })
    localStorage.setItem(LocalStorageKey.deploymentDisplayMode, newDisplayMode)
  }
}

export default injectIntl(StackDeploymentSearch)

function isDefaultEsQuery(query) {
  const emptyEsQuery = isEmptyQuery(query)

  if (emptyEsQuery) {
    return true
  }

  return isEqual(query, defaultEsQuery)
}

function getStoredDisplayMode(): 'grid' | 'table' {
  const displayMode = localStorage.getItem(LocalStorageKey.deploymentDisplayMode)

  if (displayMode != null && displayMode === 'table') {
    return 'table'
  }

  return 'grid'
}
